// src/components/CatylexSearch/catylexSearchStyles.ts
// Shared color tokens and style definitions for CatylexSearch components

import React from "react";

export const catylexColors = {
  mutedBg: "#F8F9FA",
  mutedBgHover: "#E9ECEF",
  border: "#E9ECEF",
  primary: "#106ebe",
  primaryBg: "rgba(16, 110, 190, 0.1)",
  foreground: "#1a1a1a",
  mutedForeground: "#6C757D",
  headerText: "#495057",
  separator: "#CED4DA",
  warningBg: "rgba(255, 193, 7, 0.1)",
  warningColor: "#856404",
  errorBg: "rgba(220, 53, 69, 0.08)",
  errorColor: "#721c24",
  neutralBg: "rgba(108, 117, 125, 0.1)",
  activeBg: "rgba(40, 167, 69, 0.1)",
  activeColor: "#28a745",
  inactiveColor: "#dc3545",
  quoteBorder: "rgba(16, 110, 190, 0.3)",
  quoteBg: "rgba(16, 110, 190, 0.03)",
  pageBg: "rgba(108, 117, 125, 0.08)",
  pageColor: "#495057",
  chipBg: "rgba(16, 110, 190, 0.08)",
  conceptBg: "rgba(108, 117, 125, 0.08)",
};

export const containerStyle: React.CSSProperties = {
  marginBottom: "12px",
  overflow: "hidden",
  width: "100%",
  minWidth: 0,
};

export const createHeaderStyle = (isExpanded: boolean): React.CSSProperties => ({
  boxSizing: "border-box" as const,
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  backgroundColor: catylexColors.mutedBg,
  border: `1px solid ${catylexColors.border}`,
  borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
  cursor: "pointer",
  textAlign: "left" as const,
  transition: "background-color 0.2s ease",
  fontFamily: "'Segoe UI', sans-serif",
  userSelect: "none" as const,
});

export const textContentStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
};

export const headerTextStyle: React.CSSProperties = {
  fontSize: "13px",
  lineHeight: "1.4",
  color: catylexColors.headerText,
  fontWeight: 500,
  margin: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

export const separatorStyle: React.CSSProperties = {
  color: catylexColors.separator,
  margin: "0 6px",
};

export const summaryTextStyle: React.CSSProperties = {
  fontStyle: "italic",
};

export const errorBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "10px 12px",
  borderRadius: "6px",
  backgroundColor: catylexColors.errorBg,
  fontSize: "12px",
  lineHeight: "1.5",
  color: catylexColors.errorColor,
};

export const noteBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "10px 12px",
  borderRadius: "6px",
  backgroundColor: catylexColors.mutedBg,
  border: `1px solid ${catylexColors.border}`,
  fontSize: "12px",
  lineHeight: "1.5",
  color: catylexColors.headerText,
};

export const createCollapseWrapperStyle = (isExpanded: boolean): React.CSSProperties => ({
  maxHeight: isExpanded ? "500px" : "0",
  overflow: "hidden",
  transition: "max-height 0.3s ease",
});

export const createExpandedContentStyle = (isExpanded: boolean): React.CSSProperties => ({
  backgroundColor: "#FFFFFF",
  borderLeft: isExpanded ? `1px solid ${catylexColors.border}` : "none",
  borderRight: isExpanded ? `1px solid ${catylexColors.border}` : "none",
  borderBottom: isExpanded ? `1px solid ${catylexColors.border}` : "none",
  borderRadius: isExpanded ? "0 0 8px 8px" : "0",
});

export const infoBarStyle: React.CSSProperties = {
  padding: "10px 16px",
  backgroundColor: catylexColors.mutedBg,
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "flex-start",
  gap: "12px",
  fontSize: "12px",
  color: catylexColors.mutedForeground,
};

export const infoLabelStyle: React.CSSProperties = {
  fontWeight: 600,
  color: catylexColors.headerText,
};

export const contentAreaStyle: React.CSSProperties = {
  padding: "16px",
  maxHeight: "400px",
  overflowY: "auto" as const,
  overflowX: "hidden" as const,
};
